import { Link } from "react-router-dom";
import { Cpu, Landmark, HeartPulse, ShoppingBag, Factory, Headphones, Building2, Rocket, ArrowRight } from "lucide-react";
import { FadeUp, MaskLine, SectionTag } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";

const SECTORS = [
  {
    icon: Cpu, name: "IT & Technology", placed: "160+ placed",
    body: "Full-stack engineers, DevOps, data, QA, and product — from seed-stage startups to public tech companies.",
    roles: ["Frontend / Backend", "DevOps & SRE", "Data Engineering"],
  },
  {
    icon: Landmark, name: "BFSI & Fintech", placed: "70+ placed",
    body: "Risk, compliance, payments engineering, and ops talent for banks, NBFCs, and fintech scale-ups.",
    roles: ["Risk Analysts", "Payments Eng", "Compliance"],
  },
  {
    icon: HeartPulse, name: "Healthcare & Life Sciences", placed: "50+ placed",
    body: "Clinical and non-clinical staffing — nurses, lab techs, hospital ops, and healthtech builders.",
    roles: ["Nursing", "Lab Technicians", "Hospital Ops"],
  },
  {
    icon: ShoppingBag, name: "Retail & E-commerce", placed: "60+ placed",
    body: "Category managers, supply chain leads, growth marketers, and last-mile operations at scale.",
    roles: ["Category Mgmt", "Supply Chain", "Growth"],
  },
  {
    icon: Factory, name: "Manufacturing & Engineering", placed: "30+ placed",
    body: "Production, quality, maintenance, and plant leadership for auto, electronics, and precision engineering.",
    roles: ["Production", "Quality", "Plant Leadership"],
  },
  {
    icon: Headphones, name: "BPO & KPO", placed: "50+ placed",
    body: "Voice and non-voice support, back-office ops, and team leads — volume hiring without the churn spiral.",
    roles: ["Customer Success", "Back Office", "Team Leads"],
  },
  {
    icon: Building2, name: "Real Estate", placed: "15+ placed",
    body: "Sales leaders, project managers, site engineers and channel partners for developers, brokers and property-tech firms.",
    roles: ["Sales Leaders", "Project Managers", "Site Engineers"],
  },
  {
    icon: Rocket, name: "Startups & GCCs", placed: "65+ placed",
    body: "First-hire to fifty-hire programs for startups, and full capability-center buildouts for global majors.",
    roles: ["Founding Teams", "GCC Buildout", "Leadership"],
  },
];

export default function Industries() {
  return (
    <main data-testid="industries-page" className="pt-40">
      <Seo
        title="Industries We Hire For — Samanvay HR"
        description="Specialist recruitment desks for IT, BFSI, healthcare, retail, manufacturing, BPO, real estate and startups — each recruiter owns one industry's talent pools."
      />
      <section className="max-w-7xl mx-auto px-6 pb-20">
        <SectionTag>Industries</SectionTag>
        <h1 className="font-display font-semibold tracking-tighter leading-[0.98] text-5xl sm:text-6xl lg:text-7xl text-slate-900 max-w-4xl">
          <MaskLine delay={0.15}>Deep benches in</MaskLine>
          <MaskLine delay={0.3}>
            <span className="font-editorial italic font-medium text-brand">eight arenas.</span>
          </MaskLine>
        </h1>
        <FadeUp delay={0.5} className="mt-8 max-w-2xl">
          <p className="text-lg text-slate-600 font-light leading-relaxed">
            Generalist agencies know a little about everything. Our recruiters specialize — each desk owns
            one industry, its talent pools, and its salary bands.
          </p>
        </FadeUp>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {SECTORS.map((s, i) => (
            <FadeUp key={s.name} delay={Math.min(i * 0.06, 0.3)}>
              <div
                className="group h-full rounded-3xl border border-slate-200/80 bg-white p-10 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-glow-sm"
                data-testid={`industry-card-${s.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              >
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 rounded-2xl bg-orange-50 text-brand flex items-center justify-center border border-orange-200/50 transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                    <s.icon size={22} />
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-slate-400">{s.placed}</span>
                </div>
                <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight text-slate-900">{s.name}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.body}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {s.roles.map((r) => (
                    <span key={r} className="rounded-full border border-slate-200/60 bg-slate-100 px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-slate-500">
                      {r}
                    </span>
                  ))}
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="border-t border-slate-200 py-28">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeUp>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-slate-900 leading-[1.05]">
              Don&rsquo;t see your industry?{" "}
              <span className="font-editorial italic font-medium text-brand">Ask anyway.</span>
            </h2>
            <p className="mt-6 text-slate-600">New desks open where our clients need them. Yours could be next.</p>
            <Link
              to="/contact"
              data-testid="industries-cta-btn"
              className="group mt-10 inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 text-sm font-medium text-white transition-all duration-300 hover:bg-[#E63E12] hover:shadow-glow"
            >
              Talk to a specialist
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
